import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Search, MapPin, PlusCircle, Sparkles, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { CategoryGrid } from "@/components/CategoryGrid";
import { BusinessCard } from "@/components/BusinessCard";
import { Section } from "@/components/Section";
import { DealsStrip } from "@/components/DealsStrip";
import { CardsSkeleton } from "@/components/CardsSkeleton";
import { Button } from "@/components/ui/button";

function Row({ title, subtitle, items, city, action }) {
  if (!items || items.length === 0) return null;
  return (
    <Section title={title} subtitle={subtitle} action={action}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {items.slice(0, 6).map((b, i) => <BusinessCard key={b.id} business={b} index={i} />)}
      </div>
    </Section>
  );
}

export default function CityPage() {
  const { city } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    localStorage.setItem("oyebest_city", city);
    const storedName = localStorage.getItem("oyebest_city") === city ? localStorage.getItem("oyebest_city_name") : null;
    api.get(`/city/${city}`, { params: storedName ? { name: storedName } : {} })
      .then((r) => { setData(r.data); if (r.data?.city?.name) localStorage.setItem("oyebest_city_name", r.data.city.name); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [city]);

  const cityData = data?.city;
  const comingSoon = data?.coming_soon;
  const cName = cityData?.name || city;
  useSeo(cityData?.seo_title || `Best Places & Businesses in ${cName} | OyeBest`, cityData?.seo_description || `Explore top-rated businesses and services in ${cName} on OyeBest.`);

  if (!loading && !data) {
    return <div className="max-w-3xl mx-auto px-4 py-24 text-center"><h1 className="font-display text-3xl font-bold">Something went wrong</h1><Button className="mt-6 rounded-full" onClick={() => navigate("/")}>Go Home</Button></div>;
  }

  return (
    <>
      <section className="bg-gradient-to-b from-accent to-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center gap-1.5"><MapPin className="w-4 h-4" /> Explore {cName}</p>
            {comingSoon && <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-bold"><Sparkles className="w-3 h-3" /> Coming Soon</span>}
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">Best Places & Businesses in {cName}</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            {comingSoon ? `OyeBest ${cName} mein jald aa raha hai. Apne shehar ki best jagah sabse pehle list kijiye!` : (cityData?.description || `Discover the best businesses and services in ${cName}.`)}
          </p>
          <form onSubmit={(e) => { e.preventDefault(); navigate(`/search?q=${encodeURIComponent(q)}&city=${city}`); }} className="mt-6 bg-white rounded-2xl p-2 flex gap-2 shadow-lg max-w-xl border border-border">
            <div className="flex items-center flex-1 px-3">
              <Search className="w-5 h-5 text-muted-foreground" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search in ${cName}…`} className="w-full outline-none px-3 py-2.5" data-testid="city-search-input" />
            </div>
            <Button type="submit" className="rounded-xl px-6 font-semibold" data-testid="city-search-btn">Search</Button>
          </form>
        </div>
      </section>

      <Section title="Popular Categories" subtitle={`Everything you need in ${cName}`}>
        {data && <CategoryGrid categories={data.categories} city={city} />}
      </Section>

      {data?.offers?.length > 0 && <DealsStrip offers={data.offers} />}

      {loading ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><CardsSkeleton /></div>
      ) : comingSoon ? (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center" data-testid="city-no-results">
          <div className="w-16 h-16 rounded-full bg-accent text-primary flex items-center justify-center mx-auto mb-4"><MapPin className="w-8 h-8" /></div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold">No results found in {cName} yet</h2>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">Hum abhi {cName} mein businesses add kar rahe hain. Kya aap yahan koi acchi jagah jaante hain? Sabse pehle list kijiye aur apne shehar ko OyeBest par laayein.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Button onClick={() => navigate("/list-business")} className="rounded-full gap-2 h-12 px-6 font-semibold" data-testid="citypage-add-business"><PlusCircle className="w-4 h-4" /> Add This Business</Button>
            <Button variant="outline" onClick={() => navigate("/get-your-website")} className="rounded-full gap-2 h-12 px-6 font-semibold" data-testid="citypage-get-website"><Globe className="w-4 h-4" /> Get Your Website</Button>
          </div>
        </section>
      ) : (
        <>
          <div className="bg-secondary/50">
            <Row title="Featured Businesses" subtitle="Sponsored & top picks" items={data.featured} city={city} />
          </div>
          <Row title="Top Rated" subtitle="Highest rated by customers" items={data.top_rated} city={city} />
          <div className="bg-secondary/50">
            <Row title="Popular Businesses" subtitle="Most viewed in your city" items={data.popular} city={city} />
          </div>
          <Row title="New on OyeBest" subtitle="Recently added businesses" items={data.new} city={city} />
        </>
      )}
    </>
  );
}
